(function () {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class HeroVerticalSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.pin = this.querySelector("[data-hvs-pin]");
      this.slides = Array.from(this.querySelectorAll("[data-hvs-slide]"));
      this.ticks = Array.from(this.querySelectorAll("[data-hvs-tick]"));
      this.bar = this.querySelector("[data-hvs-bar]");
      this.counter = this.querySelector("[data-hvs-counter-current]");
      this.cue = this.querySelector("[data-hvs-cue]");
      this.pauseBtn = this.querySelector("[data-hvs-pause]");

      this.total = Math.max(this.slides.length, 1);
      this.index = -1;
      this.progress = 0;
      this.paused = false;
      this.frame = null;

      this.parallax = this.dataset.parallax === "true";
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.style.setProperty("--slide-count", String(this.total));

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKey = this.onKey.bind(this);

      this.slides.forEach((slide, i) => {
        slide.style.zIndex = String(i + 1);
      });

      this.querySelectorAll("video").forEach((video) => {
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("muted", "");
      });

      this.ticks.forEach((tick) => {
        tick.addEventListener("click", () => this.scrollToSlide(Number(tick.dataset.hvsTick)));
      });

      if (this.cue) {
        this.cue.addEventListener("click", () => this.scrollToSlide(Math.min(1, this.total - 1)));
      }

      if (this.pauseBtn) {
        this.pauseBtn.addEventListener("click", () => this.togglePause());
      }

      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize, { passive: true });
      window.addEventListener("keydown", this.onKey);

      this.update();

      requestAnimationFrame(() => {
        this.classList.add("is-ready");
        this.update();
      });
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("keydown", this.onKey);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    get slideHeight() {
      return (this.pin && this.pin.offsetHeight) || window.innerHeight || 1;
    }

    get sectionTop() {
      return this.getBoundingClientRect().top + window.scrollY;
    }

    isInView() {
      const rect = this.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    onKey(event) {
      if (!this.isInView()) return;

      if (event.key === "ArrowDown" || event.key === "PageDown") {
        event.preventDefault();
        this.scrollToSlide(this.index + 1);
      }

      if (event.key === "ArrowUp" || event.key === "PageUp") {
        event.preventDefault();
        this.scrollToSlide(this.index - 1);
      }
    }

    scrollToSlide(index) {
      const target = clamp(index, 0, this.total - 1);
      window.scrollTo({
        top: this.sectionTop + target * this.slideHeight,
        behavior: this.reduceMotion ? "auto" : "smooth"
      });
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    onResize() {
      this.update();
    }

    update() {
      const height = this.slideHeight;
      const progress = clamp(-this.getBoundingClientRect().top / height, 0, this.total - 1);
      this.progress = progress;

      this.slides.forEach((slide, i) => {
        const incoming = clamp(i - progress, 0, 1);
        const covered = clamp(progress - i, 0, 1);

        slide.style.setProperty("--hvs-shift", `${(incoming * 100).toFixed(3)}%`);
        slide.style.setProperty("--hvs-depth", covered.toFixed(4));

        if (this.parallax && !this.reduceMotion) {
          slide.style.setProperty("--hvs-media-shift", `${(incoming * -14).toFixed(3)}%`);
          slide.style.setProperty("--hvs-content-shift", `${(covered * -60).toFixed(2)}px`);
        }
      });

      const index = clamp(Math.round(progress), 0, this.total - 1);
      if (index !== this.index) this.setActive(index);

      if (this.bar) {
        const filled = this.total > 1 ? progress / (this.total - 1) : 1;
        this.bar.style.transform = `scaleY(${filled.toFixed(4)})`;
      }

      if (this.cue) {
        this.cue.classList.toggle("is-hidden", progress > 0.12);
      }
    }

    setActive(index) {
      this.index = index;

      this.slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
        slide.setAttribute("aria-hidden", i === index ? "false" : "true");
      });

      this.ticks.forEach((tick, i) => {
        tick.classList.toggle("is-active", i === index);
        tick.setAttribute("aria-current", i === index ? "true" : "false");
      });

      if (this.counter) {
        this.counter.textContent = String(index + 1).padStart(2, "0");
      }

      this.syncMedia();
    }

    syncMedia() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index && !this.paused;
        const video = slide.querySelector("video");
        const iframe = slide.querySelector("iframe");

        if (video) {
          if (active) {
            const played = video.play();
            if (played && typeof played.catch === "function") played.catch(() => {});
          } else {
            video.pause();
          }
        }

        if (iframe && iframe.dataset.src) {
          if (active) {
            if (iframe.getAttribute("src") !== iframe.dataset.src) {
              iframe.src = iframe.dataset.src;
            }
            this.postIframe(iframe, "playVideo");
            this.postIframe(iframe, "play");
          } else {
            this.postIframe(iframe, "pauseVideo");
            this.postIframe(iframe, "pause");
          }
        }
      });

      this.updatePauseButton();
    }

    postIframe(iframe, command) {
      if (!iframe.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: command, args: [] }),
          "*"
        );
        iframe.contentWindow.postMessage(JSON.stringify({ method: command, value: "true" }), "*");
      } catch (error) {
        /* cross-origin players reject postMessage until they finish loading */
      }
    }

    togglePause() {
      this.paused = !this.paused;
      this.syncMedia();
    }

    updatePauseButton() {
      if (!this.pauseBtn) return;
      this.pauseBtn.classList.toggle("is-paused", this.paused);
      this.pauseBtn.setAttribute("aria-pressed", this.paused ? "true" : "false");
      this.pauseBtn.setAttribute("aria-label", this.paused ? "Play media" : "Pause media");
    }
  }

  if (!customElements.get("hero-vertical-slider")) {
    customElements.define("hero-vertical-slider", HeroVerticalSlider);
  }

  if (!window.hvsEditorBound) {
    window.hvsEditorBound = true;

    document.addEventListener("shopify:block:select", (event) => {
      const slider = event.target.closest("hero-vertical-slider");
      if (!slider || typeof slider.scrollToSlide !== "function") return;

      const slide = event.target.closest("[data-hvs-slide]");
      if (!slide) return;

      const slides = Array.from(slider.querySelectorAll("[data-hvs-slide]"));
      slider.scrollToSlide(slides.indexOf(slide));
    });
  }
})();